import { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, Users, Plus, LogOut, ChevronLeft, ChevronRight, Download, UserCog, Shield, Settings, History, MapPin, Route, Circle, ClipboardList, Bell, Menu, X, Share2, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { useLocationTracker } from '@/hooks/useLocationTracker';
import { useAppointmentTypes } from '@/hooks/useAppointmentTypes';
import AppointmentDialog from '@/components/AppointmentDialog';
import AppointmentDetailDialog from '@/components/AppointmentDetailDialog';
import TeamMembersDialog from '@/components/TeamMembersDialog';
import UserManagementDialog from '@/components/UserManagementDialog';
import TeamSettingsDialog from '@/components/TeamSettingsDialog';
import AuditLogDialog from '@/components/AuditLogDialog';
import LiveMapDialog from '@/components/LiveMapDialog';
import LocationHistoryDialog from '@/components/LocationHistoryDialog';
import OnlineUsersDialog from '@/components/OnlineUsersDialog';
import NotificationModal from '@/components/NotificationModal';
import MyAssignmentsView from '@/components/MyAssignmentsView';
import PushNotificationSettings from '@/components/PushNotificationSettings';
import DayDetailDialog from '@/components/DayDetailDialog';
import WeeklyExportDialog from '@/components/WeeklyExportDialog';
import AppointmentTypeSettingsDialog from '@/components/AppointmentTypeSettingsDialog';

export default function Dashboard({ user, onLogout }) {
  const [teams, setTeams] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTeamMembersDialog, setShowTeamMembersDialog] = useState(false);
  const [selectedTeamForMembers, setSelectedTeamForMembers] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showTeamSettings, setShowTeamSettings] = useState(false);
  const [selectedTeamForSettings, setSelectedTeamForSettings] = useState(null);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [showLiveMap, setShowLiveMap] = useState(false);
  const [showLocationHistory, setShowLocationHistory] = useState(false);
  const [showOnlineUsers, setShowOnlineUsers] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'assignments'
  const [showPushSettings, setShowPushSettings] = useState(false);
  const [showDayDetail, setShowDayDetail] = useState(false);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  const [selectedDayAppointments, setSelectedDayAppointments] = useState([]);
  const [showWeeklyExport, setShowWeeklyExport] = useState(false);
  const [showTypeSettings, setShowTypeSettings] = useState(false);

  // Get appointment types from context
  const { typesMap, getTypeInfo } = useAppointmentTypes();
  
  // Start location tracking for managers and viewers
  const { isTracking, permissionStatus, lastUpdate, isEnabled, startTracking, stopTracking } = useLocationTracker(user);
  
  const isAdmin = user?.role === 'administrator';
  const isManager = user?.role === 'manager';
  const isViewer = user?.role === 'viewer';
  const isManagerOrAdmin = isAdmin || isManager;

  // Connect to real-time notifications
  const { currentNotification, handleAcknowledge, pendingCount } = useNotifications(user);

  // Heartbeat to track online status
  const heartbeatRef = useRef(null);
  
  useEffect(() => {
    // Send heartbeat every 30 seconds
    const sendHeartbeat = async () => {
      try {
        await api.post('/auth/heartbeat');
        // Also fetch online count
        const response = await api.get('/users/online');
        setOnlineCount(response.data.count || 0);
      } catch (error) {
        console.log('Heartbeat failed:', error);
      }
    };
    
    sendHeartbeat();
    heartbeatRef.current = setInterval(sendHeartbeat, 30000);
    
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (teams.length > 0) {
      fetchAppointments();
    }
  }, [currentDate, selectedTeam, teams]);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data);
    } catch (error) {
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const params = { month };
      if (selectedTeam) {
        params.team_id = selectedTeam;
      }
      const response = await api.get('/appointments', { params });
      setAppointments(response.data);
    } catch (error) {
      toast.error('Failed to load appointments');
    }
  };

  const handleCreateAppointment = async (data) => {
    try {
      await api.post('/appointments', data);
      toast.success('Appointment created successfully');
      fetchAppointments();
      setShowAppointmentDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    }
  };

  const handleUpdateAppointment = async (id, data) => {
    try {
      await api.put(`/appointments/${id}`, data);
      toast.success('Appointment updated successfully');
      fetchAppointments();
      setShowDetailDialog(false);
      setSelectedAppointment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appointment');
    }
  };

  const handleDeleteAppointment = async (id) => {
    try {
      await api.delete(`/appointments/${id}`);
      toast.success('Appointment deleted successfully');
      fetchAppointments();
      setShowDetailDialog(false);
      setSelectedAppointment(null);
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const handleCompleteAppointment = async (id) => {
    try {
      await api.patch(`/appointments/${id}/complete`);
      toast.success('Appointment marked as completed');
      fetchAppointments();
      setShowDetailDialog(false);
      setSelectedAppointment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark appointment as completed');
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const getAppointmentsForDate = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return appointments.filter(apt => apt.date === dateStr);
  };

  const getTeamColor = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.color : '#64748b';
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleExportCalendar = async () => {
    try {
      const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const params = new URLSearchParams({ month });
      if (selectedTeam) {
        params.append('team_id', selectedTeam);
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/export/ical?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `appointments-${month}.ics`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Calendar exported successfully');
    } catch (error) {
      toast.error('Failed to export calendar');
    }
  };

  const handleManageTeamMembers = (team) => {
    setSelectedTeamForMembers(team);
    setShowTeamMembersDialog(true);
  };

  const handleTeamSettings = (team) => {
    setSelectedTeamForSettings(team);
    setShowTeamSettings(true);
  };

  const handleTeamUpdate = () => {
    fetchTeams();
    setShowTeamSettings(false);
    setSelectedTeamForSettings(null);
  };

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  const days = getDaysInMonth(currentDate);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563eb] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Header with Hamburger */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-slate-100 rounded-lg"
          data-testid="hamburger-menu"
        >
          <Menu size={24} />
        </button>
        <h1 className="text-lg font-bold text-[#0f172a]">911 Pro Home</h1>
        <div className="w-10"></div>
      </div>

      {/* Sidebar Overlay for Mobile/Tablet */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-72 sm:w-64 bg-white border-r border-slate-200 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `} 
        data-testid="sidebar"
      >
        {/* Sidebar Header */}
        <div className="p-4 sm:p-6 border-b border-slate-200">
          <div className="flex items-center justify-between lg:block">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-[#0f172a]">911 Pro Home Schedule</h1>
              <p className="text-sm text-slate-600 mt-1">{user?.name}</p>
              <p className="text-xs text-slate-500 mt-1">
                {isAdmin ? '👑 Administrator' : user?.role === 'manager' ? '⚡ Manager' : '👁️ Viewer'}
              </p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Location Sharing Status for Managers and Viewers */}
          {!isAdmin && (
            <div className="mt-3 p-2 bg-slate-50 rounded-lg" data-testid="location-status">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin size={14} className={isTracking ? 'text-green-500' : 'text-slate-400'} />
                  <span className="text-xs font-medium text-slate-600">Location</span>
                </div>
                {permissionStatus === 'denied' ? (
                  <span className="text-xs text-red-500">Blocked</span>
                ) : isTracking ? (
                  <button
                    onClick={stopTracking}
                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                    data-testid="stop-tracking-btn"
                  >
                    ● Sharing
                  </button>
                ) : (
                  <button
                    onClick={startTracking}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                    data-testid="start-tracking-btn"
                  >
                    Enable
                  </button>
                )}
              </div>
              {isTracking && lastUpdate && (
                <p className="text-[10px] text-slate-400 mt-1">
                  Last update: {lastUpdate.toLocaleTimeString()}
                </p>
              )}
              {permissionStatus === 'denied' && (
                <p className="text-[10px] text-red-400 mt-1">
                  Enable in browser settings
                </p>
              )}
            </div>
          )}
        </div>

        <nav className="flex-1 p-4 overflow-y-auto" data-testid="sidebar-nav">
          <div 
            className={`sidebar-nav-item ${viewMode === 'calendar' ? 'active' : ''}`}
            onClick={() => { setViewMode('calendar'); setSidebarOpen(false); }}
            data-testid="calendar-nav-item"
          >
            <CalendarIcon size={20} />
            <span>Calendar</span>
          </div>

          {/* My Assignments - visible to viewers (technicians) */}
          {isViewer && (
            <div
              className={`sidebar-nav-item ${viewMode === 'assignments' ? 'active' : ''}`}
              onClick={() => setViewMode('assignments')}
              data-testid="my-assignments-nav-item"
            >
              <ClipboardList size={20} />
              <span>My Assignments</span>
            </div>
          )}

          {/* Online Users - visible to all */}
          <div
            className="sidebar-nav-item"
            onClick={() => { setShowOnlineUsers(true); setSidebarOpen(false); }}
            data-testid="online-users-nav-item"
          >
            <div className="relative">
              <Users size={20} />
              {onlineCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 text-white text-[10px] rounded-full flex items-center justify-center">
                  {onlineCount}
                </span>
              )}
            </div>
            <span>Online Users</span>
          </div>

          {/* Push Notifications - visible for viewers (technicians) */}
          {isViewer && (
            <div
              className="sidebar-nav-item"
              onClick={() => { setShowPushSettings(true); setSidebarOpen(false); }}
              data-testid="push-settings-nav-item"
            >
              <Bell size={20} />
              <span>Notifications</span>
            </div>
          )}
          
          {isAdmin && (
            <div
              className="sidebar-nav-item"
              onClick={() => { setShowUserManagement(true); setSidebarOpen(false); }}
              data-testid="user-management-nav-item"
            >
              <Shield size={20} />
              <span>User Management</span>
            </div>
          )}

          {isAdmin && (
            <div
              className="sidebar-nav-item"
              onClick={() => { setShowAuditLogs(true); setSidebarOpen(false); }}
              data-testid="audit-logs-nav-item"
            >
              <History size={20} />
              <span>Audit Logs</span>
            </div>
          )}

          {isAdmin && (
            <div
              className="sidebar-nav-item"
              onClick={() => { setShowTypeSettings(true); setSidebarOpen(false); }}
              data-testid="type-settings-nav-item"
            >
              <Palette size={20} />
              <span>Appointment Colors</span>
            </div>
          )}

          {(isAdmin || isManager) && (
            <div
              className="sidebar-nav-item"
              onClick={() => { setShowLiveMap(true); setSidebarOpen(false); }}
              data-testid="live-map-nav-item"
            >
              <MapPin size={20} />
              <span>Live Map</span>
            </div>
          )}

          {(isAdmin || isManager) && (
            <div
              className="sidebar-nav-item"
              onClick={() => { setShowLocationHistory(true); setSidebarOpen(false); }}
              data-testid="route-history-nav-item"
            >
              <Route size={20} />
              <span>Route History</span>
            </div>
          )}
          
          <div className="mt-6 sm:mt-8">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 px-4 mb-3">
              Teams
            </h3>
            <button
              onClick={() => { setSelectedTeam(null); setSidebarOpen(false); }}
              className={`w-full text-left px-4 py-2 rounded-lg mb-1 font-medium ${
                selectedTeam === null
                  ? 'bg-[#2563eb] text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="all-teams-filter"
            >
              All Teams
            </button>
            <div className="max-h-[40vh] overflow-y-auto">
              {teams.map((team) => (
                <div key={team.id} className="mb-1">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { setSelectedTeam(team.id); setSidebarOpen(false); }}
                      className={`flex-1 text-left px-4 py-2 rounded-lg flex items-center gap-2 font-medium text-sm ${ 
                        selectedTeam === team.id
                          ? 'bg-[#2563eb] text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid={`team-filter-${team.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: team.color }}
                      ></div>
                      <span className="truncate">{team.name}</span>
                    </button>
                    {isManagerOrAdmin && (
                      <>
                        <button
                          onClick={() => { handleTeamSettings(team); setSidebarOpen(false); }}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 hover:text-[#2563eb]"
                          title="Team settings"
                          data-testid={`team-settings-${team.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <Settings size={16} />
                        </button>
                        <button
                          onClick={() => { handleManageTeamMembers(team); setSidebarOpen(false); }}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 hover:text-[#2563eb]"
                          title="Manage team members"
                        data-testid={`manage-members-${team.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <UserCog size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            </div>
          </div>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            onClick={onLogout}
            data-testid="logout-button"
          >
            <LogOut size={20} className="mr-3" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col pt-14 lg:pt-0">
        {viewMode === 'calendar' ? (
          <>
            {/* Header */}
            <div className="bg-white border-b border-slate-200 p-3 sm:p-6">
              <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
                <div className="flex items-center gap-2 sm:gap-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={previousMonth}
                      data-testid="previous-month-button"
                      className="h-8 w-8 sm:h-10 sm:w-10"
                    >
                      <ChevronLeft size={18} />
                    </Button>
                    <h2 className="text-xl sm:text-3xl font-bold tracking-tight text-[#0f172a] min-w-[160px] sm:min-w-[240px] text-center" data-testid="current-month">
                      {monthName}
                    </h2>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={nextMonth}
                      data-testid="next-month-button"
                      className="h-8 w-8 sm:h-10 sm:w-10"
                    >
                      <ChevronRight size={18} />
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    className="text-slate-600 hover:text-[#2563eb] hover:bg-blue-50 text-sm sm:text-base h-9 sm:h-10 px-3 sm:px-4"
                    onClick={() => setShowWeeklyExport(true)}
                    data-testid="share-weekly-button"
                  >
                    <Share2 size={16} className="mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Share Week</span>
                    <span className="sm:hidden">Share</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="text-slate-600 hover:text-[#2563eb] hover:bg-blue-50 text-sm sm:text-base h-9 sm:h-10 px-3 sm:px-4"
                    onClick={handleExportCalendar}
                    data-testid="export-calendar-button"
                  >
                    <Download size={16} className="mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Export .ics</span>
                    <span className="sm:hidden">Export</span>
                  </Button>

                  {isManagerOrAdmin && (
                    <Button
                      className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm sm:text-base h-9 sm:h-10 px-3 sm:px-4"
                      onClick={() => {
                        setSelectedDate(null);
                        setShowAppointmentDialog(true);
                      }}
                      data-testid="new-appointment-button"
                    >
                      <Plus size={16} className="mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">New Appointment</span>
                      <span className="sm:hidden">New</span>
                    </Button>
                  )}

                  {!isManagerOrAdmin && (
                    <div className="px-3 py-1.5 sm:px-4 sm:py-2 bg-slate-100 rounded-lg text-xs sm:text-sm text-slate-600">
                      View Only
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Calendar */}
            <div className="flex-1 p-2 sm:p-4 lg:p-6 overflow-auto">
              <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  {/* Weekday headers */}
                  <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                      <div key={day} className="p-1 sm:p-2 lg:p-4 text-center font-semibold text-xs sm:text-sm text-slate-600">
                        <span className="hidden sm:inline">{day}</span>
                        <span className="sm:hidden">{day.charAt(0)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7" data-testid="calendar-grid">
                    {days.map((day, index) => {
                      const dayAppointments = getAppointmentsForDate(day.date);
                      return (
                        <div
                          key={index}
                          className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''}`}
                          onClick={() => {
                            if (day.isCurrentMonth) {
                              const dayApts = getAppointmentsForDate(day.date);
                              if (dayApts.length > 0) {
                                // Show day detail if there are appointments
                                setSelectedDayDate(day.date);
                                setSelectedDayAppointments(dayApts);
                                setShowDayDetail(true);
                              } else if (isManagerOrAdmin) {
                                // Create new appointment if no appointments
                                setSelectedDate(day.date);
                                setShowAppointmentDialog(true);
                              }
                            }
                          }}
                          data-testid={`calendar-day-${day.date.getDate()}`}
                        >
                          <div className="calendar-day-header text-xs sm:text-sm flex items-center justify-between">
                            <span>{day.date.getDate()}</span>
                            {/* Busy indicator dots for mobile */}
                            {dayAppointments.length > 2 && (
                              <div className="flex gap-0.5 sm:hidden">
                                {[...Array(Math.min(dayAppointments.length, 4))].map((_, i) => (
                                  <div 
                                    key={i} 
                                    className="w-1.5 h-1.5 rounded-full bg-[#2563eb]"
                                  />
                                ))}
                                {dayAppointments.length > 4 && (
                                  <span className="text-[8px] text-slate-400">+</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="space-y-0.5 sm:space-y-1">
                            {dayAppointments.slice(0, typeof window !== 'undefined' && window.innerWidth < 640 ? 2 : 3).map((apt) => {
                              const typeInfo = getTypeInfo(apt.appointment_type);
                              const TypeIcon = typeInfo.icon;
                              return (
                                <div
                                  key={apt.id}
                                  className={`appointment-chip text-[10px] sm:text-xs ${apt.status === 'completed' ? 'opacity-60' : ''}`}
                                  style={{
                                    backgroundColor: typeInfo.bgColor,
                                    borderLeftColor: typeInfo.color,
                                    borderLeftWidth: '3px',
                                    borderLeftStyle: 'solid'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedAppointment(apt);
                                    setShowDetailDialog(true);
                                  }}
                                  data-testid={`appointment-chip-${apt.id}`}
                                >
                                  <TypeIcon size={10} className="inline-block mr-1 flex-shrink-0" style={{ color: typeInfo.color }} />
                                  <span className="hidden sm:inline">{apt.start_time} </span>
                                  <span className="truncate">{apt.title}</span>
                                </div>
                              );
                            })}
                            {dayAppointments.length > (typeof window !== 'undefined' && window.innerWidth < 640 ? 2 : 3) && (
                              <button 
                                className="text-[10px] sm:text-xs text-[#2563eb] font-medium px-1 sm:px-2 hover:underline w-full text-left"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDayDate(day.date);
                                  setSelectedDayAppointments(dayAppointments);
                                  setShowDayDetail(true);
                                }}
                              >
                                +{dayAppointments.length - (typeof window !== 'undefined' && window.innerWidth < 640 ? 2 : 3)} more
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* My Assignments View */
          <div className="flex-1 p-2 sm:p-4 lg:p-6 overflow-auto bg-slate-50">
            <div className="max-w-5xl mx-auto">
              <MyAssignmentsView
                user={user}
                teams={teams}
                onSelectAppointment={(apt) => {
                  setSelectedAppointment(apt);
                  setShowDetailDialog(true);
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Appointment Dialog */}
      <AppointmentDialog
        open={showAppointmentDialog}
        onClose={() => {
          setShowAppointmentDialog(false);
          setSelectedDate(null);
        }}
        teams={teams}
        selectedDate={selectedDate}
        onSubmit={handleCreateAppointment}
        isManagerOrAdmin={isManagerOrAdmin}
      />

      {/* Appointment Detail Dialog */}
      <AppointmentDetailDialog
        open={showDetailDialog}
        onClose={() => {
          setShowDetailDialog(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        teams={teams}
        onUpdate={handleUpdateAppointment}
        onDelete={handleDeleteAppointment}
        onComplete={handleCompleteAppointment}
        isAdmin={isManagerOrAdmin}
        userRole={user?.role}
        currentUserId={user?.id}
      />

      {/* Team Members Dialog */}
      <TeamMembersDialog
        open={showTeamMembersDialog}
        onClose={() => {
          setShowTeamMembersDialog(false);
          setSelectedTeamForMembers(null);
        }}
        team={selectedTeamForMembers}
      />

      {/* User Management Dialog */}
      {isAdmin && (
        <UserManagementDialog
          open={showUserManagement}
          onClose={() => setShowUserManagement(false)}
          currentUser={user}
        />
      )}

      {/* Team Settings Dialog */}
      <TeamSettingsDialog
        open={showTeamSettings}
        onClose={() => {
          setShowTeamSettings(false);
          setSelectedTeamForSettings(null);
        }}
        team={selectedTeamForSettings}
        onUpdate={handleTeamUpdate}
      />

      {/* Audit Logs Dialog */}
      {isAdmin && (
        <AuditLogDialog
          open={showAuditLogs}
          onClose={() => setShowAuditLogs(false)}
        />
      )}

      {/* Live Map Dialog */}
      {(isAdmin || isManager) && (
        <LiveMapDialog
          open={showLiveMap}
          onClose={() => setShowLiveMap(false)}
        />
      )}

      {/* Location History Dialog */}
      {(isAdmin || isManager) && (
        <LocationHistoryDialog
          open={showLocationHistory}
          onClose={() => setShowLocationHistory(false)}
        />
      )}

      {/* Online Users Dialog */}
      <OnlineUsersDialog
        open={showOnlineUsers}
        onClose={() => setShowOnlineUsers(false)}
      />

      {/* Notification Modal - Center of screen */}
      {currentNotification && (
        <NotificationModal
          notification={currentNotification}
          onAcknowledge={handleAcknowledge}
        />
      )}

      {/* Push Notification Settings */}
      <PushNotificationSettings
        open={showPushSettings}
        onClose={() => setShowPushSettings(false)}
        user={user}
      />

      {/* Day Detail Dialog */}
      <DayDetailDialog
        open={showDayDetail}
        onClose={() => {
          setShowDayDetail(false);
          setSelectedDayDate(null);
          setSelectedDayAppointments([]);
        }}
        date={selectedDayDate}
        appointments={selectedDayAppointments}
        teams={teams}
        onSelectAppointment={(apt) => {
          setSelectedAppointment(apt);
          setShowDetailDialog(true);
        }}
        onNewAppointment={(date) => {
          setSelectedDate(date);
          setShowAppointmentDialog(true);
        }}
        isManagerOrAdmin={isManagerOrAdmin}
      />

      {/* Weekly Export Dialog */}
      <WeeklyExportDialog
        open={showWeeklyExport}
        onClose={() => setShowWeeklyExport(false)}
        teams={teams}
      />

      {/* Appointment Type Settings Dialog */}
      {isAdmin && (
        <AppointmentTypeSettingsDialog
          open={showTypeSettings}
          onClose={() => setShowTypeSettings(false)}
        />
      )}
    </div>
  );
}